import { canResource, canSite } from '../auth/useCan'
import type { Session } from '../auth/useSession'
import { ALERT_SITES, type AlertSite } from './alerts/alertTypes'

/**
 * Who may open Reports, and on which site. Pure functions over the session so the route
 * gate (app/AppShell.tsx), the tab strip (reports/ReportsShell.tsx) and the alerts
 * report's site pills (reports/alerts/ConnectedAlertsReport.tsx) agree by construction.
 *
 * Courtesy checks only, like the rest of auth/useCan.ts: the server re-checks the
 * `reports` grant on every /api/reports* request and the alerts router checks `?site=`
 * against the account's sites (serving/alert_analytics.py's _require_alert_report_access).
 */

/** The alert-report sites this account may pick, in the report's own order (first =
 *  default). Admins see every v1 site. */
export function alertReportSites(session: Session): AlertSite[] {
  return ALERT_SITES.filter((s) => canSite(session, s))
}

/** Every tab other than Alerts reads Ultratech-only routes (`/api/reports/{name}` is gated
 *  on `require_site('ultratech')` in serving/app.py's middleware). */
export function canOpenUltratechReports(session: Session): boolean {
  return canResource(session, 'reports') && canSite(session, 'ultratech')
}

/** Reports is reachable when the account has the `reports` grant and at least one site
 *  the alerts report covers -- a Wonder-Cement-only operations user gets the Alerts tab
 *  (served by the alerts router's own per-site check), not a bounce to the live wall. */
export function canOpenReports(session: Session): boolean {
  return canResource(session, 'reports') && alertReportSites(session).length > 0
}
